import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calendar,
  Users,
  MessageSquare,
  Heart,
  ThumbsUp,
  Share2,
  Bookmark,
  TrendingUp,
  Award,
  Star,
  ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';

const CommunityPage = () => {
  const stats = [
    { label: 'Members', value: '50K+', icon: Users },
    { label: 'Discussions', value: '12K+', icon: MessageSquare },
    { label: 'Solutions', value: '8K+', icon: ThumbsUp }
  ];

  const topContributors = [
    { name: 'Sarah M.', avatar: 'SM', points: 12450, badge: 'Community Champion' },
    { name: 'Alex R.', avatar: 'AR', points: 9820, badge: 'Expert' },
    { name: 'Mike C.', avatar: 'MC', points: 8340, badge: 'Expert' },
    { name: 'Emma W.', avatar: 'EW', points: 7210, badge: 'Rising Star' },
    { name: 'David P.', avatar: 'DP', points: 6890, badge: 'Rising Star' }
  ];

  const featuredDiscussions = [
    {
      title: 'Best practices for scheduling content across time zones',
      author: 'Sarah M.',
      avatar: 'SM',
      replies: 45,
      likes: 128,
      category: 'Strategy',
      hot: true
    },
    {
      title: 'How I grew my client\'s Instagram by 500% in 3 months',
      author: 'Alex R.',
      avatar: 'AR',
      replies: 89,
      likes: 256,
      category: 'Success Stories',
      hot: true
    },
    {
      title: 'AI content generation tips and tricks',
      author: 'Mike C.',
      avatar: 'MC',
      replies: 34,
      likes: 87,
      category: 'Tips & Tricks',
      hot: false
    },
    {
      title: 'New TikTok algorithm changes - what you need to know',
      author: 'Emma W.',
      avatar: 'EW',
      replies: 67,
      likes: 145,
      category: 'Platform Updates',
      hot: true
    }
  ];

  const categories = [
    { name: 'General Discussion', count: 3245, color: 'emerald' },
    { name: 'Tips & Tricks', count: 2890, color: 'blue' },
    { name: 'Feature Requests', count: 1567, color: 'purple' },
    { name: 'Success Stories', count: 892, color: 'amber' },
    { name: 'Platform Updates', count: 456, color: 'pink' },
    { name: 'Feedback', count: 234, color: 'slate' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-slate-100 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/stellisocial-icon.svg" alt="StelliSocial" className="w-8 h-8 rounded-lg" />
              <span className="font-bold text-xl text-slate-900" style={{fontFamily: "'Tenor Sans', Georgia, serif"}}>Stelli<span className="text-emerald-500">Social</span></span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/features" className="text-slate-600 hover:text-slate-900">Features</Link>
              <Link to="/pricing" className="text-slate-600 hover:text-slate-900">Pricing</Link>
              <Link to="/blog" className="text-slate-600 hover:text-slate-900">Blog</Link>
              <Link to="/community" className="text-emerald-600 font-medium">Community</Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-emerald-500 hover:bg-emerald-600">Start Free Trial</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-purple-500 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-white/20 text-white mb-4">Community</Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Join the StelliSocial Community
          </h1>
          <p className="text-xl text-purple-100 max-w-2xl mx-auto mb-8">
            Connect with 50,000+ social media professionals, share insights, and grow together.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-purple-600 hover:bg-purple-50">
              Join Community
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-purple-600">
              Browse Discussions
            </Button>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-8 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-8">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-purple-100 mb-3">
                  <stat.icon className="w-6 h-6 text-purple-600" />
                </div>
                <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
                <p className="text-slate-600">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Featured Discussions */}
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-slate-900">Trending Discussions</h2>
                <Button variant="outline">View All</Button>
              </div>
              <div className="space-y-4">
                {featuredDiscussions.map((discussion, i) => (
                  <motion.div
                    key={discussion.title}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <Card className="border-slate-100 hover:border-purple-200 hover:shadow-md transition-all cursor-pointer">
                      <CardContent className="p-5">
                        <div className="flex items-start gap-4">
                          <Avatar className="w-10 h-10">
                            <AvatarFallback className="bg-purple-100 text-purple-600">{discussion.avatar}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className="font-semibold text-slate-900 hover:text-purple-600 transition-colors">
                                    {discussion.title}
                                  </h3>
                                  {discussion.hot && (
                                    <Badge className="bg-orange-100 text-orange-700">
                                      <TrendingUp className="w-3 h-3 mr-1" /> Hot
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 text-sm text-slate-500">
                                  <span>{discussion.author}</span>
                                  <Badge variant="secondary">{discussion.category}</Badge>
                                </div>
                              </div>
                              <div className="flex items-center gap-4 text-sm text-slate-500">
                                <span className="flex items-center gap-1">
                                  <MessageSquare className="w-4 h-4" /> {discussion.replies}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Heart className="w-4 h-4" /> {discussion.likes}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Categories */}
              <Card className="border-slate-100">
                <CardHeader>
                  <h3 className="font-semibold text-slate-900">Categories</h3>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-3">
                    {categories.map(cat => (
                      <div key={cat.name} className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
                        <span className="text-slate-700">{cat.name}</span>
                        <Badge variant="secondary">{cat.count}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Top Contributors */}
              <Card className="border-slate-100">
                <CardHeader>
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                    <Award className="w-5 h-5 text-amber-500" />
                    Top Contributors
                  </h3>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-3">
                    {topContributors.map((user, i) => (
                      <div key={user.name} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50">
                        <span className="text-sm font-medium text-slate-500 w-4">{i + 1}</span>
                        <Avatar className="w-8 h-8">
                          <AvatarFallback className="bg-purple-100 text-purple-600 text-xs">{user.avatar}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <p className="font-medium text-slate-900 text-sm">{user.name}</p>
                          <p className="text-xs text-slate-500">{user.points.toLocaleString()} points</p>
                        </div>
                        {i === 0 && <Star className="w-4 h-4 text-amber-500" />}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-purple-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Connect?</h2>
          <p className="text-purple-100 mb-8">Join our thriving community and level up your social media game.</p>
          <Link to="/register">
            <Button size="lg" className="bg-white text-purple-600 hover:bg-purple-50">
              Join Free Today
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/stellisocial-icon-square-transparent.svg" alt="StelliSocial" className="w-8 h-8 rounded-lg" />
                <span className="font-bold  text-white" style={{fontFamily: "'Tenor Sans', Georgia, serif"}}>Stelli<span className="text-emerald-400">Social</span></span>
              </div>
              <p className="text-sm">The complete social media management platform for teams.</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/features" className="hover:text-white">Features</Link></li>
                <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
                <li><Link to="/integrations" className="hover:text-white">Integrations</Link></li>
                <li><Link to="/developers" className="hover:text-white">API</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
                <li><Link to="/help" className="hover:text-white">Help Center</Link></li>
                <li><Link to="/community" className="hover:text-white">Community</Link></li>
                <li><Link to="/webinars" className="hover:text-white">Webinars</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-white">About</Link></li>
                <li><Link to="/careers" className="hover:text-white">Careers</Link></li>
                <li><Link to="/privacy" className="hover:text-white">Privacy</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-sm text-center">
            &copy; {new Date().getFullYear()} StelliSocial. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CommunityPage;
